import { DateTime } from "luxon";
import type { InvoiceData, Journal } from "./types";
import { useEffect, type RefObject } from "react";
import { currenciesMap } from "./components/invoiceArea/currencies";
import { PgliteDatabase } from "drizzle-orm/pglite";
import { migrate } from "./database/migrations";

export const PREVIEW_KEY = "preview";


export function savePreviewConfig(data: InvoiceData) {
    localStorage.setItem(PREVIEW_KEY, JSON.stringify(data));
}
export function getPreviewCfg(): InvoiceData | null {
    const data = localStorage.getItem(PREVIEW_KEY);
    if (data) return JSON.parse(data) as InvoiceData;
    return null;
}
export function deletePreviewCfg() {
    localStorage.removeItem(PREVIEW_KEY);
}
export function formatDate(targetDate: string): string {
    const givenDate = DateTime.fromISO(targetDate);
    const today = DateTime.now();
    const diffInDays = Math.round(today.diff(givenDate, "days").days);
    const diffInWeeks = Math.floor(diffInDays / 7);

    if (diffInDays === 0) {
        return "Today";
    } else if (diffInDays === 1) {
        return "A day ago";
    } else if (diffInDays < 7) {
        return `${diffInDays} days ago`;
    } else if (diffInWeeks === 1) {
        return "A week ago";
    } else if (diffInWeeks < 4) {
        return `${diffInWeeks} weeks ago`;
    } else {
        return givenDate.toFormat("MMMM dd, yyyy");
    }
}

export function formatPrice(amount: number): string {
    if (isNaN(amount)) amount = 0;
    return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function useClickOutside<T extends HTMLElement>(
    ref: RefObject<T | null>,
    callback: () => void,
    excludedClass?: string
): void {
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (ref.current && ref.current.contains(target)) return;
            if (excludedClass && target.closest(`.${excludedClass}`)) return;
            callback();
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, callback, excludedClass]);
}

export async function isDbExists(dbName: string): Promise<boolean> {
    if (!indexedDB.databases) {
        console.warn("indexedDB.databases() is not supported in this browser.");
        return false;
    }

    const databases = await indexedDB.databases();
    return databases.some(db => db.name === dbName);
}

export function sortByNumberField<T>(array: T[], field: keyof T, order: 'asc' | 'desc' = 'asc'): T[] {
    return array.sort((a, b) => {
        const valueA = a[field] as number;
        const valueB = b[field] as number;
        if (order === 'asc') {
            return valueA - valueB;
        } else {
            return valueB - valueA;
        }
    })
}

export function sortByDate<T>(array: T[], dateField: keyof T, order: 'asc' | 'desc' = 'asc'): T[] {
    return array.sort((a, b) => {
        const dateA = a[dateField] as string | number;
        const dateB = b[dateField] as string | number;

        let aDate: number;
        let bDate: number;

        if (typeof dateA === "string") {
            aDate = DateTime.fromFormat(dateA, 'dd/MM/yyyy').toMillis();
        } else {
            aDate = dateA;
        }

        if (typeof dateB === "string") {
            bDate = DateTime.fromFormat(dateB, 'dd/MM/yyyy').toMillis();
        } else {
            bDate = dateB;
        }


        if (order === 'asc') {
            return aDate - bDate;
        } else {
            return bDate - aDate;
        }
    });
}

export function createMailBody({ totalToPay, billTo, dateDue, currency, publicToken, privateToken }: InvoiceData, link: string) {
    return `Dear ${billTo},\n\nThank you for your business, always a pleasure to work with you!\n\nWe have generated a new invoice in the amount of ${currenciesMap.get(currency)?.symbol}${formatPrice(totalToPay)}\n\nWe would appreciate payment of this invoice by ${dateDue} ${(publicToken && privateToken) ? `\n\nView invoice:\n ${link}` : ""}
    `;
}
export const getFlag = (countryCode: string | null) => {
    return countryCode && countryCode !== "T1" && countryCode.length === 2
        ? String.fromCodePoint(...[...countryCode.toUpperCase()].map((x) => 0x1f1a5 + x.charCodeAt(0)))
        : "";
}

export function removeFromUrl(value: string, type: string) {
    const url = new URL(window.location.href);
    const searchParams = new URLSearchParams(url.search);
    const newParams = searchParams
        .get(type)
        ?.split(",")
        .filter((pkg) => pkg !== value);
    if (newParams) {
        searchParams.set(type, newParams.join(","));
    } else {
        searchParams.delete(type);
    }
    url.search = searchParams.toString();
    window.history.pushState({}, "", url.toString());
}

export async function migrateToLatest(db: PgliteDatabase, journal: Journal) {
    await migrate(db, {
        journal,
        callback: (tag: string) => fetch(`/migrations/${tag}.sql`).then((t) => t.blob().then((t) => t.text())),
    }, { migrationsTable: '__drizzle_migrations' });
}
export function compareVersions(latest: string, newest: string): number {
    const a = latest.split('.').map(Number);
    const b = newest.split('.').map(Number);

    for (let i = 0; i < Math.max(a.length, b.length); i++) {
        const n1 = a[i] || 0;
        const n2 = b[i] || 0;
        if (n1 > n2) return 1;
        if (n1 < n2) return -1;
    }
    return 0;
}
